import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, Button, Input, Message } from "../../../../BP2Components";
import { createNewList, editV3List, getAllActions } from "../../../../../services/restAPI/ListsV3";
import { ActionConfig, ActionsMetaResponse, listElement } from "../../ListBuildertypes";
import { normalizeAction, removeEmptyFields } from "../../ListBuilderScripts";
import { getFormStructure } from "../../../../../services/restAPI/FormsV3";
import {
   TableListProvider,
   useTableListContext,
} from "../../../../../context/TableListContext/TableListContext";
import TableFiltersController from "../../../TableWidget/TableComponents/TableFilter/TableFiltersController";
import { getListV3, getListFilters } from "../../../../../services/restAPI/ListsV2";
import { usePrevious } from "../../../../../util/customHooks";
import { FormField, FormStructure } from "../../../../../services/restAPI/FormV3Types";
import { useListBuilder } from "../../../../../context/ListBuilderContext/ListBuilderContext";
import ActionsBuilder, { ActionsBuilderHandle } from "./ActionsBuilder";

const normalizeGroups = (rawGroups: any): any[] =>
   Array.isArray(rawGroups) ? rawGroups : Object.values(rawGroups ?? {});

const FiltersInitializer: React.FC<{ listId: string }> = ({ listId }) => {
   const {
      setColumnList,
      activeTab,
      setListFilters,
      setEditedListFilters,
      groupsIsChanged,
      setGroupsIsChanged,
   } = useTableListContext();
   const prevTab = usePrevious(activeTab);

   useEffect(() => {
      if (!listId) return;
      let cancelled = false;
      getListV3(listId, 1, 1)
         .then((res) => {
            if (cancelled) return;
            if (res?.data?.columns) {
               setColumnList(res.data.columns.filter((col: any) => col.options?.visible));
            }
         })
         .catch((err) => {
            if (!cancelled) console.error(err);
         });
      return () => {
         cancelled = true;
      };
   }, [listId]);

   // Handle tab switching - skip initial mount (TableFiltersController handles initial filter load)
   useEffect(() => {
      if (!listId) return;
      let cancelled = false;

      if (activeTab === 2) {
         getListV3(listId, 1, 1)
            .then((res) => {
               if (cancelled) return;
               const groups = normalizeGroups(res?.data?.meta?.groups);
               setListFilters(groups);
               setEditedListFilters(groups);
            })
            .catch((err) => {
               if (!cancelled) console.error(err);
            });
      } else if (activeTab === 1 && prevTab === 2) {
         getListFilters(listId)
            .then((res) => {
               if (cancelled) return;
               if (res?.data) {
                  setListFilters(res.data);
                  setEditedListFilters(res.data);
               }
            })
            .catch((err) => {
               if (!cancelled) console.error(err);
            });
      }

      return () => {
         cancelled = true;
      };
   }, [activeTab, listId]);

   useEffect(() => {
      if (!groupsIsChanged || activeTab !== 2 || !listId) return;
      let cancelled = false;
      getListV3(listId, 1, 1)
         .then((res) => {
            if (cancelled) return;
            const groups = normalizeGroups(res?.data?.meta?.groups);
            setListFilters(groups);
            setEditedListFilters(groups);
            setGroupsIsChanged(false);
         })
         .catch((err) => {
            if (!cancelled) console.error(err);
         });
      return () => {
         cancelled = true;
      };
   }, [groupsIsChanged, activeTab, listId]);

   return null;
};

interface CreateNewListModalInterface {
   isOpen: boolean;
   setIsOpen: (isOpen: boolean) => void;
   setNewlyCreatedList?: (data: any) => void;
   row?: listElement;
   parentFormId?: string;
}

const CreateNewListModal: React.FC<CreateNewListModalInterface> = ({
   isOpen,
   setIsOpen,
   setNewlyCreatedList,
   row,
   parentFormId,
}) => {
   const [formData, setFormData] = useState({
      name: "",
      formId: "",
      parameters: {},
      actions: [] as ActionConfig[],
      pageSize: 10,
      addItemFormId: "",
   });

   const [errors, setErrors] = useState({
      name: false,
      formId: false,
   });

   const intl = useIntl();
   const [filtersOpen, setFiltersOpen] = useState(false);
   const [formIdError, setFormIdError] = useState<string | null>(null);
   const [formStructure, setFormStructure] = useState<FormStructure>();
   const [clickedButton, setClickedButton] = useState<FormField>();
   const [actionsMeta, setActionsMeta] = useState<ActionsMetaResponse>({});
   const [actionsMetaError, setActionsMetaError] = useState(false);
   const [isActionsMetaLoading, setIsActionsMetaLoading] = useState(false);
   const [submitError, setSubmitError] = useState<string | null>(null);
   const actionsBuilderRef = useRef<ActionsBuilderHandle>(null);

   const { queryObject, setQueryObject, refreshList } = useListBuilder();

   const fetchActionsMeta = useCallback(async () => {
      setActionsMetaError(false);
      setIsActionsMetaLoading(true);
      try {
         const res = await getAllActions();
         setActionsMeta(res?.data ?? {});
      } catch (err) {
         console.error(err);
         setActionsMetaError(true);
      } finally {
         setIsActionsMetaLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchActionsMeta();
   }, []);
   useEffect(() => {
      if (!isOpen) {
         setSubmitError(null);
         return;
      }
      setFormData({
         name: row?.name || "",
         formId: row?.presenterFormId || parentFormId || "",
         parameters: {},
         actions: (row?.actions ?? []).map(normalizeAction),
         pageSize: row?.pageSize ?? 10,
         addItemFormId: row?.addItemFormId || "",
      });
      setFiltersOpen(false);
      setFormIdError(null);
      setQueryObject({ type: "", properties: {}, subQueries: [], size: 10 });
   }, [row, parentFormId, isOpen]);

   const validateForm = () => {
      const newErrors = {
         name: !formData.name.trim(),
         formId: !formData.formId.trim(),
      };

      setErrors(newErrors);

      return !Object.values(newErrors).some(Boolean);
   };

   const handleChange = (key: string, value: any) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
   };

   const handleSubmit = async () => {
      if (!validateForm()) return;
      if (actionsBuilderRef.current && !actionsBuilderRef.current.validate()) return;

      setSubmitError(null);
      try {
         let queryType = "";
         if (formData.formId?.length) {
            try {
               const res = await getFormStructure(formData.formId);
               const { module, name } = res?.data?.type ?? {};
               queryType = module && name ? `${module}.${name}` : "";
            } catch {
               setFormIdError(
                  intl.formatMessage({
                     id: "listBuilder.invalidFormId",
                     defaultMessage: "Nieprawidłowe id",
                  }),
               );
               return;
            }
         }

         const baseData = {
            ...formData,
            parameters: {
               ...(typeof formData.parameters === "string"
                  ? JSON.parse(formData.parameters)
                  : formData.parameters),
            },
            actions:
               typeof formData.actions === "string"
                  ? JSON.parse(formData.actions)
                  : formData.actions,
         };

         if (row?.listId) {
            editV3List(removeEmptyFields(baseData), row.listId);
         } else if (setNewlyCreatedList || parentFormId) {
            await createNewList({
               ...baseData,
               ...(queryType && {
                  query: {
                     type: queryType,
                     properties: {},
                     subQueries: [],
                     size: Number(formData.pageSize),
                  },
               }),
            })?.then((res) => setNewlyCreatedList && setNewlyCreatedList(res?.data));
         }

         await refreshList();
         setIsOpen(false);
      } catch (err) {
         console.error(err);
         Message.error(intl.formatMessage({ id: "form.invalid" }));
      }
   };

   useEffect(() => {
      if (!formData.formId || formData.formId.length < 24) {
         setFormStructure(undefined);
         setQueryObject({ type: "", properties: {}, subQueries: [], size: 10 });
         return;
      }
      let cancelled = false;
      getFormStructure(formData.formId).then((response) => {
         if (!cancelled) setFormStructure(response?.data as FormStructure);
      });
      return () => {
         cancelled = true;
      };
   }, [formData.formId]);

   return (
      <Modal
         isOpen={isOpen}
         onCancel={() => setIsOpen(false)}
         topBarContent={
            row?.listId || parentFormId ? <span>Edit list</span> : <span>Create list</span>
         }
      >
         <div style={{ display: "flex", flexDirection: "column", gap: "1rem", margin: "1rem" }}>
            <div>
               <label>
                  <span style={{ color: "red" }}>*</span> Nazwa listy:
               </label>
               <Input
                  value={formData.name}
                  onChange={(e) => {
                     handleChange("name", e.target.value);
                     if (errors.name) setErrors((prev) => ({ ...prev, name: false }));
                  }}
                  placeholder={row ? row?.name : "Wpisz nazwę listy"}
                  style={{
                     border: errors.name ? "1px solid red" : undefined,
                  }}
               />
            </div>

            <div>
               <label>
                  <span style={{ color: "red" }}>*</span> Form ID:
               </label>
               <Input
                  value={formData.formId}
                  onChange={(e) => {
                     handleChange("formId", e.target.value);
                     if (errors.formId) setErrors((prev) => ({ ...prev, formId: false }));
                     if (formIdError) setFormIdError(null);
                  }}
                  placeholder={row ? row?.presenterFormId : "Wpisz Form ID"}
                  style={{
                     border: errors.formId || formIdError ? "1px solid red" : undefined,
                  }}
               />
               {formIdError && (
                  <span style={{ color: "red", fontSize: "0.875rem" }}>{formIdError}</span>
               )}
            </div>

            <div>
               <label>Page Size:</label>
               <Input
                  value={String(formData.pageSize)}
                  onChange={(e) => {
                     const parsed = parseInt(e.target.value, 10);
                     if (!isNaN(parsed)) handleChange("pageSize", parsed);
                  }}
               />
            </div>

            <div>
               <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <label>Filtry:</label>
                  {row?.listId && (
                     <Button buttonType="primary" onClick={() => setFiltersOpen((v) => !v)}>
                        {filtersOpen ? "Ukryj filtry" : "Otwórz filtry"}
                     </Button>
                  )}
               </div>
               {row?.listId ? (
                  <TableListProvider initialWidgetData={{ showListFilters: false }}>
                     <FiltersInitializer listId={row.listId} />
                     <TableFiltersController
                        listId={row.listId}
                        value={{ showListFilters: true }}
                        setWidgetSettings={() => {}}
                        defaultFilterIdFromSettings={false}
                        filtersOpen={filtersOpen}
                        setFiltersOpen={setFiltersOpen}
                        disableStoreSync
                     />
                  </TableListProvider>
               ) : (
                  <div>
                     <span>Zapisz listę aby móc dodać filtry.</span>
                  </div>
               )}
            </div>

            <div>
               <label>Add Item Form ID:</label>
               <Input
                  value={formData.addItemFormId}
                  onChange={(e) => handleChange("addItemFormId", e.target.value)}
                  placeholder="Wpisz Add Item Form ID"
               />
            </div>

            <div>
               <label>Akcje:</label>
               {isActionsMetaLoading ? (
                  <span style={{ fontSize: "0.85rem", color: "#888" }}>Ładowanie akcji…</span>
               ) : actionsMetaError ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                     <span style={{ color: "red", fontSize: "0.85rem" }}>
                        Nie udało się pobrać typów akcji.
                     </span>
                     <Button buttonType="secondary" onClick={fetchActionsMeta}>
                        Spróbuj ponownie
                     </Button>
                  </div>
               ) : (
                  <ActionsBuilder
                     ref={actionsBuilderRef}
                     actions={formData.actions}
                     onChange={(actions) => handleChange("actions", actions)}
                     actionsMeta={actionsMeta}
                  />
               )}
            </div>

            {submitError && (
               <span style={{ color: "red", fontSize: "0.85rem" }}>{submitError}</span>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
               <Button buttonType="secondary" onClick={() => setIsOpen(false)}>
                  Anuluj
               </Button>
               <Button buttonType="primary" onClick={handleSubmit}>
                  Zatwierdź
               </Button>
            </div>
         </div>
      </Modal>
   );
};

export default CreateNewListModal;
